'use client';
import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { ArrowRight, Coffee, Heart, BookOpen, Shirt, Dumbbell, Monitor } from 'lucide-react';

const PROJECT_ICONS = [Coffee, Heart, BookOpen, Shirt, Dumbbell, Monitor];
const PROJECT_COLORS = [
  { accent: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20', dot: 'bg-amber-400' },
  { accent: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/20', dot: 'bg-pink-400' },
  { accent: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/20', dot: 'bg-blue-400' },
  { accent: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/20', dot: 'bg-purple-400' },
  { accent: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/20', dot: 'bg-green-400' },
  { accent: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20', dot: 'bg-orange-400' },
];

export default function SuccessGallery() {
  const { t } = useLanguage();
  const sg = (t as any)?.successGallery;

  const projects = [
    {
      dream: sg?.p1Dream ?? 'I want to open a coffee shop',
      outcome: sg?.p1Outcome ?? 'Brew & Bloom Coffee — brand, menu, and 90-day launch plan ready',
      tag: sg?.p1Tag ?? 'Coffee Shop Startup',
      metric: sg?.p1Metric ?? 'Launched in 6 weeks',
    },
    {
      dream: sg?.p2Dream ?? 'I want to start a pet grooming business',
      outcome: sg?.p2Outcome ?? 'Pawfect Groom — full brand kit, service cards, and social content',
      tag: sg?.p2Tag ?? 'Pet Grooming Business',
      metric: sg?.p2Metric ?? 'First client in 2 weeks',
    },
    {
      dream: sg?.p3Dream ?? 'I want to publish a children\'s book',
      outcome: sg?.p3Outcome ?? 'The Little Star — story outline, character concepts, and publisher pitch',
      tag: sg?.p3Tag ?? 'Children\'s Book Series',
      metric: sg?.p3Metric ?? 'Manuscript complete',
    },
    {
      dream: sg?.p4Dream ?? 'I want to launch a clothing brand',
      outcome: sg?.p4Outcome ?? 'Threads & Soul — brand identity, product line, and Instagram strategy',
      tag: sg?.p4Tag ?? 'Clothing Brand',
      metric: sg?.p4Metric ?? 'First drop sold out',
    },
    {
      dream: sg?.p5Dream ?? 'I want to become a fitness coach',
      outcome: sg?.p5Outcome ?? 'FitWithMe — coaching packages, content calendar, and lead magnet',
      tag: sg?.p5Tag ?? 'Fitness Coach',
      metric: sg?.p5Metric ?? '20 clients in month 1',
    },
    {
      dream: sg?.p6Dream ?? 'I want to create an online course',
      outcome: sg?.p6Outcome ?? 'Master Your Craft — course outline, landing page copy, and launch email',
      tag: sg?.p6Tag ?? 'Online Course',
      metric: sg?.p6Metric ?? '100 students enrolled',
    },
  ];

  return (
    <section className="py-20 px-6 lg:px-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/3 w-80 h-80 bg-accent/4 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-700 uppercase tracking-widest text-accent mb-3 px-3 py-1 rounded-full bg-accent/10 border border-accent/20">
            {sg?.sectionLabel ?? 'Success Gallery'}
          </span>
          <h2 className="text-3xl md:text-4xl font-800 text-foreground mb-4">
            {sg?.sectionTitle ?? 'Real Dreams,'}{' '}
            <span className="gradient-text">{sg?.sectionTitleHighlight ?? 'Real Results'}</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            {sg?.sectionSubtitle ?? 'See how creators like you turned a simple idea into a complete, launch-ready project.'}
          </p>
        </div>

        {/* Bento-style grid — varied sizes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((project, i) => {
            const color = PROJECT_COLORS[i];
            const ProjectIcon = PROJECT_ICONS[i];
            const isLarge = i === 0 || i === 3;

            return (
              <div
                key={i}
                className={`glass-card rounded-2xl border border-border hover:border-primary/30 transition-all duration-300 group overflow-hidden ${
                  isLarge ? 'md:col-span-1' : ''
                }`}
              >
                {/* Top color bar */}
                <div className={`h-1.5 w-full ${color.dot}`} />

                <div className="p-6">
                  {/* Tag */}
                  <div className="flex items-center justify-between mb-5">
                    <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full ${color.bg} border ${color.border}`}>
                      <ProjectIcon size={12} className={color.accent} />
                      <span className={`text-xs font-700 ${color.accent}`}>{project.tag}</span>
                    </div>
                    <span className="text-xs text-muted-foreground/60 font-500">{project.metric}</span>
                  </div>

                  {/* Dream */}
                  <div className="mb-4">
                    <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-1.5">
                      {sg?.dreamLabel ?? 'The Dream'}
                    </p>
                    <p className="text-sm text-foreground/80 italic leading-relaxed">
                      "{project.dream}"
                    </p>
                  </div>

                  {/* Arrow divider */}
                  <div className="flex items-center gap-2 my-4">
                    <div className="flex-1 h-px bg-border" />
                    <div className={`w-6 h-6 rounded-full ${color.bg} border ${color.border} flex items-center justify-center`}>
                      <ArrowRight size={10} className={color.accent} />
                    </div>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Outcome */}
                  <div>
                    <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-1.5">
                      {sg?.outcomeLabel ?? 'The Outcome'}
                    </p>
                    <p className="text-sm text-foreground leading-relaxed font-500">
                      {project.outcome}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
